import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * List variant — mirrors `components/products/card/ProductCardMobile.tsx`.
 * Image-left / content-right row used at mobile breakpoints. The width,
 * gap, padding, border-radius, and action-button shape all match the
 * real card so the layout doesn't shift when products load in.
 */
function ProductCardSkeletonList() {
  return (
    <div className="flex items-stretch gap-2 min-[400px]:gap-3 overflow-hidden rounded-xl border border-border/60 bg-card">
      <Skeleton className="aspect-square w-24 min-[400px]:w-28 shrink-0 rounded-none" />
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-1 py-2 pr-2 min-[400px]:py-2.5 min-[400px]:pr-3">
        <div>
          <div className="flex flex-wrap gap-1">
            <Skeleton className="h-3.5 w-12 rounded-full" />
            <Skeleton className="h-3.5 w-16 rounded-full" />
          </div>
          <div className="mt-1 space-y-1">
            <Skeleton className="h-4 w-[85%]" />
            <Skeleton className="h-4 w-[55%]" />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-5 w-14" />
          <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * Card-grid skeleton — mirrors `components/products/card/ProductCard.tsx`.
 * Square image / badge row / 2-line title / 2-line description /
 * price + round icon footer. Sizes and tokens (`border-border/60`,
 * `px-3 pt-3 pb-2`, `border-border/40`, footer button as round icon)
 * line up with the real card so there's no jump on hydrate.
 */
export function ProductCardSkeleton({ variant }: { variant?: "card" | "list" } = {}) {
  if (variant === "list") return <ProductCardSkeletonList />;
  return (
    <Card className="flex flex-col overflow-hidden border-border/60 bg-card gap-0 pt-0 md:py-0">
      <Skeleton className="aspect-square w-full shrink-0 rounded-none" />
      <CardContent className="flex flex-1 flex-col gap-1.5 px-3 pt-3 pb-2">
        <div className="flex flex-wrap gap-1.5">
          <Skeleton className="h-4.5 w-14 rounded-full" />
          <Skeleton className="h-4.5 w-20 rounded-full" />
          <Skeleton className="h-4.5 w-16 rounded-full" />
        </div>
        <div className="space-y-1">
          <Skeleton className="h-5 w-[85%]" />
          <Skeleton className="h-5 w-[60%]" />
        </div>
        <div className="mt-1 min-h-10 space-y-1">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-[80%]" />
        </div>
      </CardContent>
      <CardFooter className="flex flex-row items-center justify-between gap-2 border-t border-border/40 px-3 py-2.5">
        <Skeleton className="h-6 w-14" />
        {/* Match the real action: round icon button (44px floor on
            mobile, 36px on sm+) — not a wide rectangle. */}
        <Skeleton className="h-11 w-11 shrink-0 rounded-full sm:h-9 sm:w-9" />
      </CardFooter>
    </Card>
  );
}
